import * as React from 'react'

import { Box, Button, type IllustrationType } from '@factorialco/deprecated-design-system'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import type { Result, UnhandledResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import type { NewTeam } from 'modules/teams/hooks/useCreateTeam'
import type { TeamBasicInfo } from 'modules/teams/hooks/useTeamDetail'
import { TeamFormValues } from 'modules/teams/lib/forms'
import teamsCollection from 'modules/teams/store/collections/teams'

interface Props {
  team?: TeamBasicInfo
  title: string
  illustration: IllustrationType
  loading?: boolean
  onBlurName: () => void
  onBlurDescription: () => void
  onClose: () => void
  onSubmit: (data: TeamFormValues) => Promise<UnhandledResult<Result<unknown>>>
  onError: (formErrors: Record<string, string>) => void
  onSuccess: (data: TeamBasicInfo) => void
}

const TeamModalForm = ({
  team,
  title,
  illustration,
  onBlurName,
  onBlurDescription,
  loading = false,
  onClose,
  onSubmit,
  onError,
  onSuccess,
}: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const form = useForm<TeamFormValues>({
    defaultValues: {
      name: '',
      description: '',
    },
    entity: {
      ...team,
    },
    validate: (data) => {
      const errors: FormErrors<TeamFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await onSubmit({ ...data })
      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ name: formErrors.name })
        onError(formErrors)
      })

      if (handled.success) {
        const team = (handled.data as NewTeam).team
        if (team) {
          await teamsCollection.fetch()
          onSuccess(team)
        }
      }

      return handled
    },
  })

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        title={title}
        description={i18n.t('teams.define_team')}
        illustration={illustration}
        onClose={onClose}
        onSubmit={() => form.handleSubmit()}
        footer={
          <>
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={loading || !form.isValid} submit>
              {i18n.t('common.save')}
            </Button>
          </>
        }
      >
        <Box gap="s24">
          <TextField
            mandatory
            label={i18n.t('teams.name')}
            error={form.errors.name}
            value={form.values.name}
            onBlur={onBlurName}
            onChange={(value: string) => form.setValues({ ...form.values, name: value })}
          />
          <TextArea
            label={i18n.t('teams.description_label')}
            value={form.values.description}
            onBlur={onBlurDescription}
            onChange={(value: string) => form.setValues({ ...form.values, description: value })}
          />
        </Box>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default TeamModalForm
